import {DestroyRef, Injectable, computed, inject, signal} from "@angular/core"
import {graphNode} from "@cm/graph-v3"
import {
    TemplateNode,
    TemplateListNode,
    templateListNodeDefinition,
    isTemplateNode,
    Parameters,
    TemplateInstance,
    TemplateGraph,
    isNamedNode,
    isIdNode,
    MaterialAssignment,
    MaterialAssignments,
    TemplateGraphInput,
    TemplateGraphOutput,
    TemplateGraphSwitch,
    TemplateGraphExport,
    ConfigGroup,
    ConfigVariant,
    isNodeOwnerLikeNode,
    SwitchNode,
    OutputNode,
    isTemplateOwningContainer,
    isTemplateReferenceContainer,
} from "@cm/template-nodes"

import {v4 as uuid4} from "uuid"

import {removeUndefinedEntriesFromObject} from "@cm/utils"

// Transient list used to bundle nodes for a single clone() pass — not registered, not serialized.
class GatherTemplateNode extends TemplateListNode<TemplateNode> {
    public override get definition() {
        return templateListNodeDefinition("GatherTemplateNode", graphNode(TemplateNode))
    }
}

@Injectable({providedIn: "root"})
export class TemplateNodeClipboardService {
    private readonly $_nodes = signal<TemplateNode[]>([])
    $nodes = this.$_nodes.asReadonly()
    readonly $valid = computed(() => this.$_nodes().length > 0)
    private readonly destroyRef = inject(DestroyRef)

    clear(): void {
        this.$_nodes.set([])
    }

    copy(nodes: TemplateNode | TemplateNode[]) {
        if (Array.isArray(nodes)) this.$_nodes.set([...nodes])
        else this.$_nodes.set([nodes])
    }

    pasteReferences() {
        return [...this.$_nodes()]
    }

    pasteDuplicates() {
        const gatherTemplateNode = new GatherTemplateNode({parameters: {list: this.pasteReferences()}})
        const rootNodes = new Set([...gatherTemplateNode.entries])
        const gatherTemplateNodeCopy = gatherTemplateNode.clone({
            cloneSubNode: (parent, subNode) => {
                const cloneSubNode = (() => {
                    if (isTemplateNode(parent) && isTemplateNode(subNode)) {
                        if (gatherTemplateNode.entries.includes(subNode)) return true

                        //Clone all node owning nodes
                        if (isNodeOwnerLikeNode(subNode)) {
                            //Unless not used as a direct template graph reference
                            if (subNode instanceof TemplateGraph) {
                                if (
                                    parent instanceof TemplateGraphExport ||
                                    parent instanceof TemplateGraphInput ||
                                    parent instanceof TemplateGraphOutput ||
                                    parent instanceof TemplateGraphSwitch ||
                                    parent instanceof TemplateInstance
                                ) {
                                    return false
                                }
                            }
                            return true
                        }
                        //Clone node container nodes
                        if (isNodeOwnerLikeNode(parent) && isTemplateOwningContainer(subNode)) return true
                        //Also clone referenced nodes of the containers
                        if (isTemplateOwningContainer(parent)) return true

                        //Clone material assignments (in meshes)
                        if (subNode instanceof MaterialAssignments) return true
                        //Clone all material assignment items within material assignments, but do not clone the referenced nodes of the containers
                        if (subNode instanceof MaterialAssignment) return true

                        //Clone switch nodes
                        if (subNode instanceof SwitchNode) return true
                        if (parent instanceof SwitchNode && isTemplateReferenceContainer(subNode)) return true
                        if (isTemplateReferenceContainer(parent)) return true

                        //Clone parameters (in template instance)
                        if (subNode instanceof Parameters) return true

                        //Clone outputs
                        if (subNode instanceof OutputNode) return true
                    }

                    return false
                })()

                return cloneSubNode
            },
            parameterOverrides: (node) => {
                if (isTemplateNode(node))
                    return removeUndefinedEntriesFromObject({
                        name: isNamedNode(node) && rootNodes.has(node) ? node.parameters.name : undefined,
                        id: isIdNode(node) && !(node instanceof ConfigGroup || node instanceof ConfigVariant) ? uuid4() : undefined,
                    })
                else return undefined
            },
        })

        gatherTemplateNode.clear()

        const returnNodes = [...gatherTemplateNodeCopy.entries]

        gatherTemplateNodeCopy.clear()

        return returnNodes
    }
}
